import React, { useState } from 'react';
import { Mail, Send, Paperclip, FileText, FileSpreadsheet, Loader2, CheckCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { shareScenarioEmail } from '../lib/api';

const EmailShareDialog = ({ open, onOpenChange, scenario }) => {
  const [email, setEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [includePdf, setIncludePdf] = useState(true);
  const [includeExcel, setIncludeExcel] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const handleSend = async () => {
    if (!email) {
      setError('Please enter a recipient email address');
      return;
    }

    setSending(true);
    setError(null);

    try {
      await shareScenarioEmail({
        recipient_email: email,
        recipient_name: recipientName || null,
        scenario_id: scenario.id,
        include_pdf: includePdf,
        include_excel: includeExcel,
        personal_message: personalMessage || null,
      });
      
      setSent(true);
      setTimeout(() => {
        onOpenChange(false);
        setSent(false);
        setEmail('');
        setRecipientName('');
        setPersonalMessage('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const results = scenario?.results || {};
  const roi = results.roi_percent || results.total_roi_percent || 0;
  const kpiUplift = results.kpi_delta_percent || results.total_kpi_delta_percent || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-testid="email-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-teal-600" />
            Share Scenario via Email
          </DialogTitle>
          <DialogDescription>
            Send "{scenario?.name}" to a colleague with attached reports.
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="py-8 text-center">
            <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-800">Email Sent!</h3>
            <p className="text-slate-500">The report has been sent to {email}</p>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4">
              {/* Recipient Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Recipient Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="colleague@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="share-email-input"
                />
              </div>

              {/* Recipient Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Recipient Name (optional)</Label>
                <Input
                  id="name"
                  placeholder="John Smith"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                />
              </div>

              {/* Personal Message */}
              <div className="space-y-2">
                <Label htmlFor="message">Personal Message (optional)</Label>
                <Textarea
                  id="message"
                  placeholder="I thought you might find this analysis interesting..."
                  value={personalMessage}
                  onChange={(e) => setPersonalMessage(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Attachments */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  Attachments
                </Label>
                <div className="flex flex-col gap-2 pl-6">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="pdf"
                      checked={includePdf}
                      onCheckedChange={setIncludePdf}
                    />
                    <label htmlFor="pdf" className="text-sm flex items-center gap-2 cursor-pointer">
                      <FileText className="h-4 w-4 text-rose-500" />
                      PDF Report
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="excel"
                      checked={includeExcel}
                      onCheckedChange={setIncludeExcel}
                    />
                    <label htmlFor="excel" className="text-sm flex items-center gap-2 cursor-pointer">
                      <FileSpreadsheet className="h-4 w-4 text-emerald-500" />
                      Excel Report
                    </label>
                  </div>
                </div>
              </div>

              {/* Preview Summary */}
              <div className="bg-slate-50 rounded-lg p-3 border">
                <p className="text-xs text-slate-500 mb-2">Email will include:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-500">ROI:</span>
                    <span className="ml-2 font-medium text-teal-600">{roi.toFixed(1)}%</span>
                  </div>
                  <div>
                    <span className="text-slate-500">KPI Uplift:</span>
                    <span className="ml-2 font-medium">{kpiUplift.toFixed(2)}%</span>
                  </div>
                </div>
              </div>

              {error && (
                <p className="text-sm text-rose-500">{error}</p>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSend} 
                disabled={sending || !email}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="send-email-btn"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmailShareDialog;
